/**
 * Authed fetch helpers for ingest job corpus documents
 * (`GET /api/ontology/ingest/jobs/:jobId/source`).
 *
 * Same header pattern as reports/download: an `<iframe src>` / `<img src>`
 * cannot send `X-API-Key`, so the viewer fetches bytes and builds an object URL.
 */

import { getOntologyServiceBaseUrl } from '../../config'
import { createOntologyHeaders } from '../core'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

/** Server served a generated workbook PDF instead of the original file. */
export const SOURCE_REPRESENTATION_WORKBOOK_PDF = 'workbook-pdf'
/** Server served a generated office-document PDF instead of the original file. */
export const SOURCE_REPRESENTATION_OFFICE_PDF = 'office-pdf'

export type SourceRepresentation =
  | typeof SOURCE_REPRESENTATION_WORKBOOK_PDF
  | typeof SOURCE_REPRESENTATION_OFFICE_PDF

/** 404 from the source route — no corpus document for this job/path. */
export class SourceDocUnavailableError extends Error {
  readonly status = 404

  constructor(message = 'Document is not available for this job') {
    super(message)
    this.name = 'SourceDocUnavailableError'
  }
}

export interface FetchIngestSourceOptions {
  /**
   * When true, skip PDF remap and always download the original
   * corpus bytes (`?original=1`).
   */
  original?: boolean
}

export interface FetchIngestSourceResult {
  blob: Blob
  contentType: string
  /** Present when the server remapped a source file to its generated PDF. */
  representation?: SourceRepresentation
  /** Normalized corpus path of the original file when remapped. */
  originalPath?: string
}

/** Build the BFF path for an ingest source document (path query unencoded). */
export function ingestSourcePath(
  jobId: string,
  path: string,
  options?: FetchIngestSourceOptions
): string {
  const params = new URLSearchParams()
  params.set('path', path)
  if (options?.original) params.set('original', '1')
  return `/ingest/jobs/${encodeURIComponent(jobId)}/source?${params.toString()}`
}

function parseRepresentation(header: string | null | undefined): SourceRepresentation | undefined {
  const value = header?.trim()
  if (value === SOURCE_REPRESENTATION_WORKBOOK_PDF) return SOURCE_REPRESENTATION_WORKBOOK_PDF
  if (value === SOURCE_REPRESENTATION_OFFICE_PDF) return SOURCE_REPRESENTATION_OFFICE_PDF
  return undefined
}

/**
 * Fetch corpus document bytes for a job. Maps HTTP 404 to
 * {@link SourceDocUnavailableError}; other non-OK responses throw with body text.
 * Remappable paths may return a generated PDF — see
 * {@link FetchIngestSourceResult.representation}.
 */
export async function fetchIngestSource(
  jobId: string,
  path: string,
  options?: FetchIngestSourceOptions
): Promise<FetchIngestSourceResult> {
  // Body-less GET: strip Content-Type so Fastify doesn't try to parse an empty JSON body.
  const headers = createOntologyHeaders()
  headers.delete('Content-Type')
  const res = await fetch(`${ONTOLOGY_SERVICE_URL}${ingestSourcePath(jobId, path, options)}`, {
    headers
  })
  if (res.status === 404) {
    throw new SourceDocUnavailableError()
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`source document fetch failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  const contentType = res.headers.get('Content-Type')?.split(';')[0]?.trim() || ''
  const representation = parseRepresentation(res.headers.get('X-Axilon-Source-Representation'))
  const originalPath = res.headers.get('X-Axilon-Source-Original-Path')?.trim()
  return {
    blob: await res.blob(),
    contentType,
    ...(representation ? { representation } : {}),
    ...(originalPath ? { originalPath } : {})
  }
}
